import React, { useState, useRef, useEffect } from "react";
import { HubConnectionBuilder, HttpTransportType } from "@microsoft/signalr";
import { useAuth } from "../AuthProvider";
import { RIDE_MAP_PIN_COLORS } from "../config/rideMap";

function pinIconUrl(fillColor) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="56" viewBox="0 0 24 36"><path fill="${fillColor}" stroke="#ffffff" stroke-width="1.2" d="M12 1.2C6.15 1.2 1.4 5.95 1.4 11.8c0 8.35 10.6 22.4 10.6 22.4s10.6-14.05 10.6-22.4C22.6 5.95 17.85 1.2 12 1.2z"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function createLabeledPin({ position, map, letter, title, fillColor }) {
  return new window.google.maps.Marker({
    position,
    map,
    title,
    label: { text: letter, color: "#ffffff", fontWeight: "700" },
    icon: {
      url: pinIconUrl(fillColor),
      scaledSize: new window.google.maps.Size(32, 44),
      anchor: new window.google.maps.Point(16, 44),
      labelOrigin: new window.google.maps.Point(16, 13),
    },
  });
}

function DetailsRequests(props) {
  const auth = useAuth();
  const googleMapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [hubConnection, setHubConnection] = useState(null);

  console.log(props);

  const rideStarted = props?.item?.status !== "in progress";

  const [driverMarker, setDriverMarker] = useState(null);
  const [passengerMarker, setPassengerMarker] = useState(null);
  const [originMarker, setOriginMarker] = useState(null);
  const [destinationMarker, setDestinationMarker] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);

  const [driverLocation, setDriverLocation] = useState({
    latitude: props?.item?.latitudeFrom || 0,
    longitude: props?.item?.longitudeFrom || 0,
  });
  const [passengerLocation, setPassengerLocation] = useState({
    latitude: props?.item?.latitudeTo || 0,
    longitude: props?.item?.longitudeTo || 0,
  });

  useEffect(() => {
    const createHubConnection = async () => {
      const connection = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_BASE_URL.replace(/\/api\/v2$/, "")}/rideHub`, {
          accessTokenFactory: () => auth.token,
          headers: {
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
          transport: HttpTransportType.WebSockets,
          skipNegotiation: true,
        })
        .withAutomaticReconnect()
        .build();

      try {
        // Listen for driver location updates
        connection.on("ReceiveRideUpdate", (data) => {
          console.log(data);
          setDriverLocation({
            latitude: data.driverLatitude,
            longitude: data.driverLongitude,
          });

          setPassengerLocation({
            latitude: data.passengerLatitude,
            longitude: data.passengerLongitude,
          });
        });

        console.log("SignalR Connected!");

        // Join the ride group
        // 0: rides , 1 :city rides

        await connection.start();
        await connection.invoke("JoinRideGroup", props?.item?.uuid, props?.type === "city_ride" ? 1 : 0);

        setInterval(async () => {
          if (connection.state === "Connected") {
            await connection.invoke("Ping");
          }
        }, 10000);

        setHubConnection(connection);
      } catch (err) {
        console.error("SignalR Connection Error: ", err);
      }
    };

    if (props?.item?.uuid) {
      createHubConnection();
    }

    return () => {
      if (hubConnection) {
        hubConnection.stop();
      }
    };
  }, [props?.item?.uuid, auth.token]);

  useEffect(() => {
    if (!props?.item?.uuid) {
      return;
    }

    const googleMap = initGoogleMap();
    setMap(googleMap);
  }, [props?.item?.uuid]);

  useEffect(() => {
    if (!map) return;

    // Create directions service and renderer
    if (!directionsRenderer) {
      const renderer = new window.google.maps.DirectionsRenderer({
        map: map,
        suppressMarkers: true, // Don't show default A/B markers
        preserveViewport: true, // Don't auto-zoom
      });
      setDirectionsRenderer(renderer);

      const directionsService = new window.google.maps.DirectionsService();

      // Calculate and display route
      directionsService.route(
        {
          origin: { lat: props?.item?.latitudeFrom, lng: props?.item?.longitudeFrom },
          destination: { lat: props?.item?.latitudeTo, lng: props?.item?.longitudeTo },
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === "OK") {
            renderer.setDirections(result);
          } else {
            console.error("Directions request failed:", status);
          }
        }
      );
    }

    // Update driver marker
    if (driverMarker) {
      driverMarker.setPosition({
        lat: driverLocation.latitude,
        lng: driverLocation.longitude,
      });
    } else {
      const marker = new window.google.maps.Marker({
        position: {
          lat: driverLocation.latitude,
          lng: driverLocation.longitude,
        },
        map,
        icon: {
          url: "https://maps.google.com/mapfiles/ms/icons/cabs.png", // Car icon
          scaledSize: new window.google.maps.Size(32, 32), // Adjust size as needed
        },
        title: "Driver Location",
      });
      setDriverMarker(marker);
    }

    // Update passenger marker only if ride hasn't started
    if (!rideStarted) {
      if (passengerMarker) {
        passengerMarker.setPosition({
          lat: passengerLocation.latitude,
          lng: passengerLocation.longitude,
        });
      } else {
        const marker = new window.google.maps.Marker({
          position: {
            lat: passengerLocation.latitude,
            lng: passengerLocation.longitude,
          },
          map,
          label: "P",
          title: "Passenger Location",
        });
        setPassengerMarker(marker);
      }
    } else if (passengerMarker) {
      // Remove passenger marker if ride has started
      passengerMarker.setMap(null);
      setPassengerMarker(null);
    }

    if (!originMarker) {
      setOriginMarker(
        createLabeledPin({
          position: {
            lat: props?.item?.latitudeFrom,
            lng: props?.item?.longitudeFrom,
          },
          map,
          letter: "A",
          title: "Pickup Location",
          fillColor: RIDE_MAP_PIN_COLORS.pickup,
        })
      );
    }

    if (!destinationMarker) {
      setDestinationMarker(
        createLabeledPin({
          position: {
            lat: props?.item?.latitudeTo,
            lng: props?.item?.longitudeTo,
          },
          map,
          letter: "B",
          title: "Drop-off Location",
          fillColor: RIDE_MAP_PIN_COLORS.dropoff,
        })
      );
    }
  }, [map, driverLocation, passengerLocation, rideStarted]);

  // Cleanup effect for the directions renderer
  useEffect(() => {
    return () => {
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
      }
    };
  }, [directionsRenderer]);

  const initGoogleMap = () => {
    const bounds = new window.google.maps.LatLngBounds();
    const map = new window.google.maps.Map(googleMapRef.current, {
      zoom: 8,
    });

    // Add all points to bounds
    bounds.extend({ lat: props?.item?.latitudeFrom, lng: props?.item?.longitudeFrom });
    bounds.extend({ lat: props?.item?.latitudeTo, lng: props?.item?.longitudeTo });
    bounds.extend({ lat: driverLocation.latitude, lng: driverLocation.longitude });
    bounds.extend({ lat: passengerLocation.latitude, lng: passengerLocation.longitude });

    // Fit the map to show all markers
    map.fitBounds(bounds);

    return map;
  };

  return (
    <>
      <div ref={googleMapRef} className="google-map" />
    </>
  );
}

export default DetailsRequests;
